import { useMemo, useRef } from 'react';

import { useGLTF } from '@react-three/drei';
import { Box3, Color, Mesh, Object3D, Vector3 } from 'three';

const SPIGOT_MODEL_PATH = '/models/SSC1625N5.glb';
const SPIGOT_TARGET_HEIGHT = 4.5;
const SPIGOT_ROTATION_Y = Math.PI / 2;
const SPIGOT_GLASS_SEAT_RATIO = 0.55;

interface GlassPanelProps {
  position: [number, number, number];
  rotation: [number, number, number];
  dimensions: [number, number, number];
  type?: 'glass' | 'door';
  material: 'black' | 'silver';
}

interface SpigotModelProps {
  position: [number, number, number];
  material: 'black' | 'silver';
}

function SpigotModel({ position, material }: SpigotModelProps) {
  const { scene } = useGLTF(SPIGOT_MODEL_PATH);
  const tintColor = material === 'black' ? '#1a1a1a' : '#a8a8a8';

  const { clonedScene, scale } = useMemo(() => {
    const model = scene.clone(true);
    const tint = new Color(tintColor);

    model.traverse((child: Object3D) => {
      if (!(child instanceof Mesh)) {
        return;
      }

      child.castShadow = true;
      child.receiveShadow = true;
      child.material = Array.isArray(child.material)
        ? child.material.map((meshMaterial) => meshMaterial.clone())
        : child.material.clone();

      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((meshMaterial) => {
        const editableMaterial = meshMaterial as {
          color?: Color;
          metalness?: number;
          roughness?: number;
        };

        if (editableMaterial.color) {
          editableMaterial.color.copy(tint);
        }
        if (typeof editableMaterial.metalness === 'number') {
          editableMaterial.metalness = 0.9;
        }
        if (typeof editableMaterial.roughness === 'number') {
          editableMaterial.roughness = material === 'black' ? 0.35 : 0.25;
        }
      });
    });

    const bounds = new Box3().setFromObject(model);
    const size = new Vector3();
    bounds.getSize(size);
    const modelHeight = size.y || 1;
    const uniformScale = SPIGOT_TARGET_HEIGHT / modelHeight;

    model.position.x -= bounds.min.x + size.x / 2;
    model.position.y -= bounds.min.y;
    model.position.z -= bounds.min.z + size.z / 2;

    return { clonedScene: model, scale: uniformScale };
  }, [material, scene, tintColor]);

  return (
    <group
      position={position}
      rotation={[0, SPIGOT_ROTATION_Y, 0]}
      scale={[scale, scale, scale]}
    >
      <primitive object={clonedScene} />
    </group>
  );
}

export function GlassPanel({
  position,
  rotation,
  dimensions,
  type = 'glass',
  material,
}: GlassPanelProps) {
  const meshRef = useRef<Mesh>(null);
  const isDoor = type === 'door';
  
  const spigotOffset = dimensions[0] / 4;
  const glassOffsetY = SPIGOT_TARGET_HEIGHT * SPIGOT_GLASS_SEAT_RATIO;

  // Render a single spigot model
  const renderSpigot = (xPosition: number) => (
    <SpigotModel position={[xPosition, -dimensions[1] / 2, 0]} material={material} />
  );

  return (
    <group position={position} rotation={rotation}>
      <group position={[0, glassOffsetY, 0]}>
        {/* Main frameless glass panel */}
        <mesh ref={meshRef} castShadow receiveShadow>
          <boxGeometry args={dimensions} />
          <meshPhysicalMaterial
            color="#e8f4f8"
            transparent
            opacity={0.35}
            metalness={0}
            roughness={0.02}
            transmission={0.85}
            thickness={dimensions[2]}
            envMapIntensity={1}
            clearcoat={1}
            clearcoatRoughness={0.1}
            ior={1.52}
            reflectivity={0.5}
          />
        </mesh>

      </group>

      {/* Spigot 1 (Left) */}
      {!isDoor && renderSpigot(-spigotOffset)}

      {/* Spigot 2 (Right) */}
      {!isDoor && renderSpigot(spigotOffset)}
    </group>
  );
}

useGLTF.preload(SPIGOT_MODEL_PATH);
